import * as React from 'react';
import Box from '@mui/material/Box';
import RtlButton from './RtlButton';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router';
import dayjs, { Dayjs } from 'dayjs';
import type { Employee } from '../data/employees';
import { useLanguage } from '../../contexts/LanguageContext';
import {ArrowForward} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { useAuth } from '../../contexts/AuthContext';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import { getAllBranches } from '../data/branches';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';

export interface EmployeeFormState {
  values: Partial<Omit<Employee, 'id'>> & {
    contract_image?: File;
    contract_image_url?: string;
    profile_image?: File;
    profile_image_url?: string;
    role_name?: string;
  };
  errors: Partial<Record<keyof EmployeeFormState['values'], string>> & {
    contract_image?: string;
    profile_image?: string;
  };
}

export type FormFieldValue = string | string[] | number | boolean | File | null;

export interface EmployeeFormProps {
  formState: EmployeeFormState;
  onFieldChange: (
    name: keyof EmployeeFormState['values'],
    value: FormFieldValue,
  ) => void;
  onSubmit: (formValues: Partial<EmployeeFormState['values']>) => Promise<void>;
  onReset?: (formValues: Partial<EmployeeFormState['values']>) => void;
  submitButtonLabel: string;
  backButtonPath?: string;
  isCreateMode?: boolean;
  onValidate?: (values: Partial<EmployeeFormState['values']>) => { issues: { message: string; path: (string | number)[] }[] };
}

export default function EmployeeForm(props: EmployeeFormProps) {
  const {
    formState,
    onFieldChange,
    onSubmit,
    onReset,
    submitButtonLabel,
    backButtonPath,
    isCreateMode = false,
  } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;

  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [branches, setBranches] = React.useState<Array<{ id: number; name: string }>>([]);
  const [loadingBranches, setLoadingBranches] = React.useState(true);

  // Password generation function
  const generatePassword = React.useCallback(() => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  }, []);

  React.useEffect(() => {
    const fetchBranches = async () => {
      try {
        const branchesData = await getAllBranches();
        setBranches(branchesData);

        // For BM users, set branch to their own branch
        if (user?.role === 'BM' && user.branch_id) {
          onFieldChange('branch_id', user.branch_id);
        }
        // Otherwise, set first branch as default if no branch is selected and branches exist
        else if (branchesData.length > 0 && !formValues.branch_id) {
          onFieldChange('branch_id', branchesData[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      } finally {
        setLoadingBranches(false);
      }
    };
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setIsSubmitting(true);
      try {
        await onSubmit(formValues);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formValues, onSubmit],
  );

  const handleTextFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof EmployeeFormState['values'],
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleNumberFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      onFieldChange(
        event.target.name as keyof EmployeeFormState['values'],
        value === '' ? undefined : Number(value),
      );
    },
    [onFieldChange],
  );

  const handleCheckboxFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onFieldChange(event.target.name as keyof EmployeeFormState['values'], checked);
    },
    [onFieldChange],
  );

  const handleDateFieldChange = React.useCallback(
    (fieldName: keyof EmployeeFormState['values']) => (value: Dayjs | null) => {
      if (value?.isValid()) {
        onFieldChange(fieldName, value.toISOString() ?? null);
      } else if (formValues[fieldName]) {
        onFieldChange(fieldName, null);
      }
    },
    [formValues, onFieldChange],
  );

  const handleSelectFieldChange = React.useCallback(
    (event: SelectChangeEvent) => {
      onFieldChange(
        event.target.name as keyof EmployeeFormState['values'],
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleContractImageChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        onFieldChange('contract_image', files[0]);
      }
    },
    [onFieldChange],
  );

  const handleRemoveContractImage = React.useCallback(() => {
    onFieldChange('contract_image', null);
    onFieldChange('contract_image_url', null);
  }, [onFieldChange]);

  const handleProfileImageChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        onFieldChange('profile_image', files[0]);
      }
    },
    [onFieldChange],
  );

  const handleRemoveProfileImage = React.useCallback(() => {
    onFieldChange('profile_image', null);
    onFieldChange('profile_image_url', null);
  }, [onFieldChange]);


  const handleGeneratePassword = React.useCallback(() => {
    const newPassword = generatePassword();
    onFieldChange('password', newPassword);
  }, [generatePassword, onFieldChange]);

  const handleReset = React.useCallback(() => {
    if (onReset) {
      onReset(formValues);
    }
  }, [formValues, onReset]);

  const handleBack = React.useCallback(() => {
    navigate(backButtonPath ?? '/employees');
  }, [navigate, backButtonPath]);

  const { direction } = useLanguage();
  const isRtl = direction === 'rtl';
  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.name ?? ''}
              onChange={handleTextFieldChange}
              name="name"
              label={t('employees.name')}
              error={!!formErrors.name}
              helperText={formErrors.name ?? ' '}
              fullWidth
              InputLabelProps={{
                sx: {
                  transformOrigin: isRtl ? 'top right' : 'top left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.email ?? ''}
              onChange={handleTextFieldChange}
              name="email"
              label={t('employees.email')}
              type="email"
              error={!!formErrors.email}
              helperText={formErrors.email ?? ' '}
              fullWidth
              InputLabelProps={{
                sx: {
                  transformOrigin: isRtl ? 'top right' : 'top left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.password ?? ''}
              onChange={handleTextFieldChange}
              name="password"
              label={`${t('employees.password')}${!isCreateMode ? ` (${t('common.optional')})` : ''}`}
              type='text'
              error={!!formErrors.password}
              helperText={formErrors.password ?? ' '}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ marginLeft: 0, marginRight: 0 }}>
                    <Tooltip title={t('employees.generatePassword')}>
                      <IconButton
                        onClick={handleGeneratePassword}
                        edge="end"
                        size="small"
                        sx={{ backgroundColor: 'transparent', border: '0 !important' }}
                      >
                        <AutorenewIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                sx: {
                  transformOrigin: isRtl ? 'top right' : 'top left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
            <FormControl error={!!formErrors.branch_id} fullWidth>
              <InputLabel
                id="employee-branch-label"
                sx={{
                  transformOrigin: isRtl ? 'top right' : 'top left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0
                }}
              >
                {t('employees.branch')}
              </InputLabel>
              <Select
                value={formValues.branch_id ?? ''}
                onChange={handleSelectFieldChange as SelectProps['onChange']}
                labelId="employee-branch-label"
                name="branch_id"
                label={t('employees.branch')}
                disabled={loadingBranches || user?.role === 'BM'}
                fullWidth
                sx={{ backgroundColor: 'transparent' }}
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{
                textAlign: isRtl ? 'right' : 'left',
                marginRight: isRtl ? '14px' : 0,
                marginLeft: isRtl ? 0 : '14px'
              }}>{formErrors.branch_id ?? ' '}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
            <FormControl error={!!formErrors.role_name} fullWidth>
              <InputLabel
                id="employee-role-label"
                sx={{
                  transformOrigin: isRtl ? 'top right' : 'top left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0
                }}
              >
                {t('employees.role')}
              </InputLabel>
              <Select
                value={formValues.role_name ?? ''}
                onChange={handleSelectFieldChange as SelectProps['onChange']}
                labelId="employee-role-label"
                name="role_name"
                label={t('employees.role')}
                defaultValue="Employee"
                fullWidth
                sx={{ backgroundColor: 'transparent' }}
              >
                <MenuItem value="GM">{t('employees.roles.admin')}</MenuItem>
                <MenuItem value="BM">{t('employees.roles.branchManager')}</MenuItem>
                <MenuItem value="Employee">{t('employees.roles.employee')}</MenuItem>
              </Select>
              <FormHelperText sx={{
                textAlign: isRtl ? 'right' : 'left',
                marginRight: isRtl ? '14px' : 0,
                marginLeft: isRtl ? 0 : '14px'
              }}>{formErrors.role_name ?? ' '}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
            <TextField
              type="number"
              value={formValues.salary ?? ''}
              onChange={handleNumberFieldChange}
              name="salary"
              label={t('employees.salary')}
              error={!!formErrors.salary}
              helperText={formErrors.salary ?? ' '}
              fullWidth
              InputLabelProps={{
                sx: {
                  transformOrigin: isRtl ? 'top right' : 'top left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
            <TextField
              type="number"
              value={formValues.available_leave ?? ''}
              onChange={handleNumberFieldChange}
              name="available_leave"
              label={t('employees.availableLeave')}
              error={!!formErrors.available_leave}
              helperText={formErrors.available_leave ?? ' '}
              fullWidth
              disabled
              InputLabelProps={{
                sx: {
                  transformOrigin: isRtl ? 'top right' : 'top left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0
                }
              }}
              FormHelperTextProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  marginRight: isRtl ? '14px' : 0,
                  marginLeft: isRtl ? 0 : '14px'
                }
              }}
            />
          </Grid>

          {/* Profile Image Upload Section */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {t('employees.profileImage')}
            </Typography>
            <RtlButton
              variant="outlined"
              component="label"
              startIcon={<AddPhotoAlternateIcon />}
              sx={{ mb: 2 }}
            >
              {t('employees.addProfileImage')}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleProfileImageChange}
              />
            </RtlButton>
            {formErrors.profile_image && (
              <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
                {formErrors.profile_image}
              </Typography>
            )}

            {/* Existing Profile Image */}
            {formValues.profile_image_url && !formValues.profile_image && (
              <Card sx={{ maxWidth: 200, mb: 2 }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={formValues.profile_image_url}
                  alt="Profile Image"
                  sx={{ objectFit: 'cover', marginBottom: '5px', borderRadius: '8px' }}
                />
              </Card>
            )}

            {/* New Profile Image Preview */}
            {formValues.profile_image && (
              <Card sx={{ maxWidth: 200 }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={URL.createObjectURL(formValues.profile_image as File)}
                  alt="Profile Image Preview"
                  sx={{ objectFit: 'cover', marginBottom: '5px', borderRadius: '8px' }}
                />
              </Card>
            )}
          </Grid>

          {/* Contract Image Upload Section */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {t('employees.contractImage')}
            </Typography>
            <RtlButton
              variant="outlined"
              component="label"
              startIcon={<AddPhotoAlternateIcon />}
              sx={{ mb: 2 }}
            >
              {t('employees.addContractImage')}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleContractImageChange}
              />
            </RtlButton>
            {formErrors.contract_image && (
              <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
                {formErrors.contract_image}
              </Typography>
            )}

            {/* Existing Contract Image */}
            {formValues.contract_image_url && !formValues.contract_image && (
              <Card sx={{ maxWidth: 400, mb: 2 }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={formValues.contract_image_url}
                  alt="Contract Image"
                  sx={{ objectFit: 'cover', marginBottom: '5px', borderRadius: '8px' }}
                />
              </Card>
            )}

            {/* New Contract Image Preview */}
            {formValues.contract_image && (
              <Card sx={{ maxWidth: 400 }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={URL.createObjectURL(formValues.contract_image as File)}
                  alt="Contract Image Preview"
                  sx={{ objectFit: 'cover', marginBottom: '5px', borderRadius: '8px' }}
                />
              </Card>
            )}
          </Grid>
        </Grid>
      </FormGroup>
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <RtlButton
          type="submit"
          variant="contained"
          size="large"
          loading={isSubmitting}
          sx={{ background: '#028bcb !important', boxShadow: 'unset !important', border: '0 !important', '&:hover': { background: '#0277a8 !important' } }}
        >
          {submitButtonLabel}
        </RtlButton>
      </Stack>
    </Box>
  );
}
